import React, { useState, useCallback, useEffect } from 'react';
import {
  ControlButton,
  Edge,
  ReactFlow,
  ReactFlowInstance,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { EmployeePosition, PositionFlowNode } from './types';
import { PositionNodeComponent } from './PositionNodeComponent';
import { getLayoutedElements, isPositionSelectable } from './utils';
import { ZOOM_LEVELS } from './constants';

const nodeTypes = {
  positionNode: PositionNodeComponent,
};

type PositionFlowInstance = ReactFlowInstance<PositionFlowNode, Edge>;

const useFlowPositioning = (
  reactFlowInstance: PositionFlowInstance | undefined,
  nodes: PositionFlowNode[],
  positions: EmployeePosition[],
  selectedPositionId: string,
  showFullHierarchy: boolean,
) => {
  useEffect(() => {
    if (!reactFlowInstance || !nodes.length) return;

    const currentNode = nodes.find((node) =>
      positions.find((pos) => pos.Id === node.id && pos.isCurrentPosition),
    );
    const selectedNode = nodes.find((node) => node.id === selectedPositionId);

    const centerOnNode = (node: PositionFlowNode, zoom: number) => {
      const x = node.position.x + 90;
      const y = node.position.y + 50;
      reactFlowInstance.setCenter(x, y, { zoom, duration: 800 });
    };

    setTimeout(() => {
      if (
        (!selectedPositionId || selectedPositionId === currentNode?.id) &&
        currentNode?.position
      ) {
        centerOnNode(
          currentNode,
          showFullHierarchy ? ZOOM_LEVELS.full : ZOOM_LEVELS.focused,
        );
        return;
      }

      if (selectedNode?.position) {
        if (showFullHierarchy) {
          centerOnNode(selectedNode, ZOOM_LEVELS.focused);
          return;
        }

        // Focus on the selected position regardless of tree relationship
        centerOnNode(selectedNode, ZOOM_LEVELS.focused);
        return;
      }

      if (currentNode?.position) {
        centerOnNode(
          currentNode,
          showFullHierarchy ? ZOOM_LEVELS.full : ZOOM_LEVELS.focused,
        );
        return;
      }

      reactFlowInstance.fitView({
        padding: showFullHierarchy ? 0.1 : 0.15,
        duration: 800,
        minZoom: ZOOM_LEVELS.full,
        maxZoom: ZOOM_LEVELS.maximum,
      });
    }, 200);
  }, [
    reactFlowInstance,
    nodes,
    positions,
    selectedPositionId,
    showFullHierarchy,
  ]);

  useEffect(() => {
    if (!reactFlowInstance || !nodes.length) return;

    const selectedNode = nodes.find((node) => node.id === selectedPositionId);

    if (selectedNode?.position) {
      setTimeout(() => {
        const transform = reactFlowInstance.getViewport();
        const nodeX = selectedNode.position.x + 80;
        const nodeY = selectedNode.position.y + 45;

        reactFlowInstance.setCenter(nodeX, nodeY, {
          zoom: Math.max(transform.zoom, ZOOM_LEVELS.focused),
          duration: 600,
        });
      }, 150);
    }
  }, [selectedPositionId, reactFlowInstance, nodes]);
};

export const FlowContent: React.FC<
  React.PropsWithChildren<{
    positions: EmployeePosition[];
    selectedPositionId: string;
    onPositionSelect: (positionId: string) => void;
    displayStaffedPositions: boolean;
    showFullHierarchy: boolean;
    t: (key: string, options?: { defaultValue?: string }) => string;
  }>
> = ({
  positions,
  selectedPositionId,
  onPositionSelect,
  displayStaffedPositions,
  showFullHierarchy,
  t,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<PositionFlowNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<PositionFlowInstance>();

  useEffect(() => {
    const flowNodes: PositionFlowNode[] = positions.map((position) => ({
      id: position.Id,
      type: 'positionNode',
      position: { x: 0, y: 0 },
      data: {
        position,
        isSelected: selectedPositionId === position.Id,
        onSelect: () => onPositionSelect(position.Id),
        displayStaffedPositions,
        t,
      },
    }));

    const flowEdges: Edge[] = positions
      .filter((position) => position.flair__Parent_Position__c)
      .map((position) => {
        const parentPosition = positions.find(
          (p) => p.Id === position.flair__Parent_Position__c,
        );
        return parentPosition
          ? {
              id: `e${parentPosition.Id}-${position.Id}`,
              source: parentPosition.Id,
              target: position.Id,
              type: 'smoothstep',
              style: { stroke: 'var(--bs-gray-500)', strokeWidth: 1 },
            }
          : null;
      })
      .filter(Boolean) as Edge[];

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      flowNodes,
      flowEdges,
    );
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [
    positions,
    selectedPositionId,
    onPositionSelect,
    setNodes,
    setEdges,
    displayStaffedPositions,
    t,
  ]);

  useFlowPositioning(
    reactFlowInstance,
    nodes,
    positions,
    selectedPositionId,
    showFullHierarchy,
  );

  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: PositionFlowNode) => {
      event.stopPropagation();
      const position = positions.find((p) => p.Id === node.id);
      if (!position || !isPositionSelectable(position)) return;
      onPositionSelect(node.id);
    },
    [onPositionSelect, positions],
  );

  const focusOnCurrentPosition = useCallback(() => {
    if (!reactFlowInstance || !nodes.length) return;

    const currentNode = nodes.find((node) =>
      positions.find((pos) => pos.Id === node.id && pos.isCurrentPosition),
    );

    if (currentNode?.position) {
      const x = currentNode.position.x + 90;
      const y = currentNode.position.y + 50;
      const zoom = ZOOM_LEVELS.focused;
      reactFlowInstance.setCenter(x, y, { zoom, duration: 800 });
    } else {
      reactFlowInstance.fitView({
        padding: showFullHierarchy ? 0.1 : 0.15,
        duration: 800,
        minZoom: ZOOM_LEVELS.full,
        maxZoom: ZOOM_LEVELS.maximum,
      });
    }
  }, [reactFlowInstance, nodes, positions, showFullHierarchy]);

  const onInit = useCallback(
    (instance: PositionFlowInstance) => {
      setReactFlowInstance(instance);

      setTimeout(() => {
        const currentNode = nodes.find((node) =>
          positions.find((pos) => pos.Id === node.id && pos.isCurrentPosition),
        );

        if (currentNode?.position) {
          const x = currentNode.position.x + 90;
          const y = currentNode.position.y + 50;
          const zoom = showFullHierarchy
            ? ZOOM_LEVELS.full
            : ZOOM_LEVELS.focused;
          instance.setCenter(x, y, { zoom, duration: 800 });
        } else {
          instance.fitView({
            padding: showFullHierarchy ? 0.1 : 0.15,
            duration: 800,
            minZoom: ZOOM_LEVELS.full,
            maxZoom: ZOOM_LEVELS.maximum,
          });
        }
      }, 300);
    },
    [nodes, positions, showFullHierarchy],
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={handleNodeClick}
      onInit={onInit}
      nodeTypes={nodeTypes}
      nodesDraggable={false}
      elementsSelectable={true}
      className="bg-light"
      selectNodesOnDrag={false}
      minZoom={0.1}
      maxZoom={ZOOM_LEVELS.maximum}
      defaultViewport={{
        x: 0,
        y: 0,
        zoom: showFullHierarchy ? ZOOM_LEVELS.full : ZOOM_LEVELS.focused,
      }}>
      <div className="react-flow__controls">
        <ControlButton
          onClick={focusOnCurrentPosition}
          title="Focus on current position">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"
              fill="currentColor"
            />
          </svg>
        </ControlButton>
      </div>
    </ReactFlow>
  );
};
